import { createRouter, createWebHistory } from 'vue-router'

import { api } from '@/lib/api'
import { useAuthStore } from '@/stores/auth'

export const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      name: 'landing',
      component: () => import('@/views/LandingView.vue'),
      meta: { guestOnly: true },
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/LoginView.vue'),
      meta: { guestOnly: true },
    },
    {
      path: '/register',
      name: 'register',
      component: () => import('@/views/RegisterView.vue'),
      meta: { guestOnly: true },
    },
    {
      // Where signing in lands. The terminal is the densest screen on the
      // platform and assumes you already know what you are doing; this is the
      // page between the front door and that.
      path: '/discover',
      name: 'discover',
      component: () => import('@/views/DiscoverView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/terminal',
      name: 'terminal',
      component: () => import('@/views/TerminalView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/leaderboard',
      name: 'leaderboard',
      component: () => import('@/views/LeaderboardView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/settlements',
      name: 'settlements',
      component: () => import('@/views/SettlementsView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/history',
      name: 'history',
      component: () => import('@/views/HistoryView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('@/views/ForgotPasswordView.vue'),
      meta: { guestOnly: true },
    },
    {
      // Reached from an email, so it must work whether or not the browser
      // that opens it has a session.
      path: '/reset-password',
      name: 'reset-password',
      component: () => import('@/views/ResetPasswordView.vue'),
    },
    {
      path: '/verify',
      name: 'verify-email',
      component: () => import('@/views/VerifyEmailView.vue'),
    },
    {
      // The account *menu*, as distinct from /account which is the settings
      // form it links into. Splitting them is why the menu can stay scannable.
      path: '/profile',
      name: 'profile',
      component: () => import('@/views/ProfileView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/account',
      name: 'account',
      component: () => import('@/views/AccountView.vue'),
      meta: { requiresAuth: true },
    },
    {
      // The referral programme, from the earning side.
      //
      // Its own route rather than a tab inside the wallet: a trader shares
      // this link with people, and "open your wallet, then find the referrals
      // tab" is not a thing anyone repeats accurately.
      path: '/referrals',
      name: 'referrals',
      component: () => import('@/views/ReferralsView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/wallet',
      name: 'wallet',
      component: () => import('@/views/WalletView.vue'),
      meta: { requiresAuth: true },
    },
    // There is deliberately no /admin route here.
    //
    // The operations console is a separate application with its own entry
    // point, served on its own port and not exposed through the public edge.
    // Keeping it out of this router is what stops its code being bundled into
    // what every trader downloads — and what stops the door being on the
    // street at all. The server-side checks remain the real defence; this is
    // the layer in front of them.
    { path: '/:pathMatch(.*)*', redirect: '/' },
  ],
  scrollBehavior: () => ({ top: 0 }),
})

/**
 * The end of a session, handled once and centrally.
 *
 * Every screen makes authenticated calls, so leaving each one to notice its
 * own 401 means either repeating this in a dozen places or - what actually
 * happened - nowhere at all. The router is the one place that can both clear
 * the store and move the user somewhere useful.
 *
 * `next` is preserved so signing in returns them to the screen they were on
 * rather than a default page, and guest routes are left alone: a session
 * ending while somebody reads the landing page should not throw a login form
 * at them.
 */
api.onSessionExpired(() => {
  const auth = useAuthStore()
  auth.forget()
  const current = router.currentRoute.value
  if (current.meta.requiresAuth) {
    void router.replace({ name: 'login', query: { next: current.fullPath, expired: '1' } })
  }
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()

  // On a hard refresh the store has not resolved yet. Waiting here rather than
  // guessing is what stops a signed-in user being redirected to the landing
  // page before their profile has loaded.
  if (!auth.initialised) await auth.initialise()

  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    // Remember where they were going, so signing in lands them there rather
    // than dumping them on a default page.
    return { name: 'login', query: { next: to.fullPath } }
  }

  // A signed-in user has no use for the landing or login pages.
  if (to.meta.guestOnly && auth.isAuthenticated && api.isAuthenticated) {
    return { name: 'discover' }
  }

  return true
})
